/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd. <legal@vecros.com>
 * Proprietary and confidential.
 **********************************************************************************/

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { streamMessage } from "./model/assistantClient";
import { getChat } from "./model/assistantHistory";
import { useUserContext } from "../../context/UserContext";
import { ROUTES } from "../../routes";

/**
 * Single source of truth for the assistant: open/close, mode, the active thread,
 * streaming lifecycle and message actions. UI components read this and stay dumb.
 */
const AssistantContext = createContext(null);

// Destinations the assistant may navigate to (drives the backend `navigate` tool).
const ASSISTANT_ROUTES = [
  { name: "dashboard", label: "Dashboard", path: ROUTES.home, description: "Home / developer dashboard" },
  { name: "profile", label: "Profile", path: ROUTES.profile, description: "Your account details" },
  { name: "documentation", label: "Documentation", path: ROUTES.docs, description: "Developer documentation" },
  { name: "downloads", label: "Downloads", path: ROUTES.downloads, description: "SDKs & downloads" },
  { name: "devices", label: "Devices", path: ROUTES.devices, description: "Registered devices" },
  { name: "thrustStand", label: "Thrust Stand", path: ROUTES.thrustStand, description: "Thrust stand tools" },
  { name: "logAnalyzer", label: "Log Analyzer", path: ROUTES.logAnalyzer, description: "Flight log analysis" },
  { name: "propulsionCalc", label: "Propulsion Calculator", path: ROUTES.propulsionCalc, description: "Motor, prop and battery sizing" },
  { name: "motorData", label: "Motor Data", path: ROUTES.motorData, description: "Claimed vs measured motor test data" },
  { name: "firmwareFlasher", label: "Firmware Flasher", path: ROUTES.firmwareFlasher, description: "Flash ArduPilot firmware over USB" },
  { name: "billing", label: "Billing", path: ROUTES.plansBilling, description: "Payments & invoices" },
  { name: "plans", label: "Plans", path: ROUTES.plans, description: "Subscription plans" },
];

/** A short, low-PII summary of the signed-in user, sent to ground the assistant. */
function buildAccountContext(u) {
  if (!u) return null;
  const parts = [];
  const name = [u.user_first_name, u.user_last_name].filter(Boolean).join(" ");
  if (name) parts.push(`Name: ${name}`);
  if (u.user_email) parts.push(`Email: ${u.user_email}`);
  if (u.country) parts.push(`Country: ${u.country}`);
  return parts.length ? parts.join(" · ") : null;
}

export const useAssistant = () => {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error("useAssistant must be used within <AssistantProvider>");
  return ctx;
};

let idSeq = 0;
const nextId = () => `m${++idSeq}-${Math.floor(performance.now())}`;

export function AssistantProvider({ children, initialOpen = false, onRequestClose }) {
  const [open, setOpen] = useState(initialOpen);
  const [mode, setMode] = useState("docked"); // 'docked' | 'focus'
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(null);
  const { userDetails } = useUserContext();
  const navigate = useNavigate();
  const accountContext = useMemo(() => buildAccountContext(userDetails), [userDetails]);

  const [pendingSeed, setPendingSeed] = useState(null);
  const openPanel = useCallback((seedPrompt) => {
    setOpen(true);
    if (seedPrompt) {
      setPendingSeed(seedPrompt);
    }
  }, []);
  const closePanel = useCallback(() => {
    setOpen(false);
    if (onRequestClose) onRequestClose();
  }, [onRequestClose]);
  const toggleMode = useCallback(
    () => setMode((m) => (m === "docked" ? "focus" : "docked")),
    []
  );

  const newChat = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setStreaming(false);
    setChatId(null);
    setMessages([]);
    setPendingSeed(null);
  }, []);

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  /** Send a user turn and consume the streamed assistant reply. */
  const send = useCallback(
    async (text, context = []) => {
      const content = (text || "").trim();
      if (!content || streaming) return;

      const userMsg = {
        id: nextId(),
        role: "user",
        content,
        context,
        createdAt: Date.now(),
      };
      const assistantMsg = {
        id: nextId(),
        role: "assistant",
        content: "",
        steps: [],
        rating: null,
        createdAt: Date.now(),
        streaming: true,
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      const patch = (fn) =>
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantMsg.id ? fn(m) : m))
        );

      try {
        for await (const ev of streamMessage({
          message: content,
          assistantChatId: chatId,
          context,
          accountContext,
          routes: ASSISTANT_ROUTES,
          signal: controller.signal,
        })) {
          switch (ev.type) {
            case "chat":
              if (ev.assistantChatId) setChatId(ev.assistantChatId);
              break;
            case "action":
              // navigate: mode 'go' drives the UI now; mode 'suggest' attaches a
              // button to this message the user can click when ready.
              if (ev.action === "navigate" && ev.path) {
                const route = ASSISTANT_ROUTES.find(
                  (r) => r.name === ev.destination || r.path === ev.path
                );
                const label =
                  route?.label || route?.description || ev.destination || "there";
                if (ev.mode === "go") {
                  navigate(ev.path);
                  patch((m) => ({
                    ...m,
                    steps: [
                      ...(m.steps || []),
                      { label: `Opened ${label}`, status: "success" },
                    ],
                  }));
                } else {
                  patch((m) => ({ ...m, nav: { path: ev.path, label } }));
                }
              }
              break;
            case "step":
              patch((m) => ({
                ...m,
                steps: [...(m.steps || []), { label: ev.label, status: ev.status }],
              }));
              break;
            case "token":
              patch((m) => ({ ...m, content: m.content + (ev.text || "") }));
              break;
            case "error":
              patch((m) => ({
                ...m,
                error: { code: ev.code, message: ev.message },
                streaming: false,
              }));
              break;
            case "done":
            default:
              break;
          }
        }
        patch((m) => ({ ...m, streaming: false }));
      } catch (err) {
        if (err && err.name === "AbortError") {
          patch((m) => ({ ...m, streaming: false }));
        } else {
          patch((m) => ({
            ...m,
            streaming: false,
            error: { code: "STREAM_FAILED", message: "Something went wrong. Try again." },
          }));
        }
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [chatId, streaming, accountContext, navigate]
  );

  /** Optimistic per-message rating (feeds the future rateChat endpoint). */
  const rate = useCallback((messageId, rating) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId ? { ...m, rating: m.rating === rating ? null : rating } : m
      )
    );
  }, []);

  const consumeSeed = useCallback(() => {
    const s = pendingSeed;
    setPendingSeed(null);
    return s;
  }, [pendingSeed]);

  /** Load a stored conversation into the panel and open it. */
  const loadChat = useCallback(async (id) => {
    if (!id) return;
    if (abortRef.current) abortRef.current.abort();
    setStreaming(false);
    try {
      const chat = await getChat(id);
      const loaded = (chat?.messages || []).map((m, i) => ({
        id: `h${i}-${id}`,
        role: m.role,
        content: m.content,
        createdAt: m.created_at ? new Date(m.created_at).getTime() : Date.now(),
      }));
      setChatId(id);
      setMessages(loaded);
      setPendingSeed(null);
      setOpen(true);
    } catch (_) {
      /* best-effort — leave the current conversation intact on failure */
    }
  }, []);

  /** Navigate the app (used by a message's suggested-navigation button). */
  const navigateTo = useCallback((path) => {
    if (path) navigate(path);
  }, [navigate]);

  const value = useMemo(
    () => ({
      open,
      mode,
      chatId,
      messages,
      streaming,
      pendingSeed,
      openPanel,
      closePanel,
      toggleMode,
      newChat,
      send,
      stop,
      rate,
      consumeSeed,
      navigateTo,
      loadChat,
    }),
    [
      open,
      mode,
      chatId,
      messages,
      streaming,
      pendingSeed,
      openPanel,
      closePanel,
      toggleMode,
      newChat,
      send,
      stop,
      rate,
      consumeSeed,
      navigateTo,
      loadChat,
    ]
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}
